/** @file Sockets layer: grip axis a -> b with a diamond at a, a dot at b and a ring on the grip-2 bone. */
import type { SocketData } from '@pose2d/core/types';
import type { Sink } from '../draw/createSink';
import { emitDisc } from '../draw/emitDisc';
import { emitLine } from '../draw/emitLine';
import { emitQuad } from '../draw/emitQuad';
import { emitRing } from '../draw/emitRing';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { boneSegment } from './boneSegment';

const seg = new Float64Array(4);
const diamond = new Float64Array(8);
const R = 5;

/** Writes the diamond of radius R around (ax, ay): top, right, bottom, left. */
function fillDiamond(ax: number, ay: number): Float64Array {
  diamond[0] = ax;
  diamond[1] = ay - R;
  diamond[2] = ax + R;
  diamond[3] = ay;
  diamond[4] = ax;
  diamond[5] = ay + R;
  diamond[6] = ax - R;
  diamond[7] = ay;
  return diamond;
}

/** One socket: the a -> b axis, a diamond at a, a dot at b, and a ring on its grip-2 bone when it has one. */
function drawSocket(sink: Sink, { pose, camera }: OverlayInput, socket: SocketData): void {
  boneSegment(pose, camera, socket.bone, socket.length, seg);
  const ax = seg[0] ?? NaN, ay = seg[1] ?? NaN;
  emitLine(sink, seg, 2, OVERLAY_STYLE.socket);
  emitQuad(sink, fillDiamond(ax, ay), OVERLAY_STYLE.socket);
  emitDisc(sink, seg[2] ?? NaN, seg[3] ?? NaN, 3, OVERLAY_STYLE.socket);
  if (socket.grip2Bone < 0) return;
  boneSegment(pose, camera, socket.grip2Bone, 0, seg);
  emitRing(sink, seg[0] ?? NaN, seg[1] ?? NaN, R, 1.5, OVERLAY_STYLE.socket);
}

/** `options.sockets`: every socket (its bone, length and optional grip-2 bone). Names come from collectNames. */
export function drawSockets(sink: Sink, input: OverlayInput): void {
  if (input.options.sockets !== true) return;
  const sockets = input.data.sockets;
  for (let k = 0; k < sockets.length; k++) {
    const socket = sockets[k];
    if (socket !== undefined) drawSocket(sink, input, socket);
  }
}
